import AirbnbNew from '@/../img/airbnb_new.svg';
import FlipkeyNew from '@/../img/flipkey_new.svg';
import HomeawayNew from '@/../img/homeaway_new.svg';
import NonClickableAirbnbIcon from '@/../img/non-clickable-airbnb.svg';
import NonClickableBookingIcon from '@/../img/non-clickable-booking.svg';
import NonClickableFlipkeyIcon from '@/../img/non-clickable-flipkey.svg';
import NonClickableHomeawayIcon from '@/../img/non-clickable-homeaway.svg';
import NonClickableTripAdvisorIcon from '@/../img/non-clickable-tripadvisor.svg';
import NonClickableVrboIcon from '@/../img/non-clickable-vrbo.svg';
import TripadvisorNew from '@/../img/tripadvisor_new.svg';
import VrboNew from '@/../img/vrbo_new.svg';
import appSettings from '@/app-settings.json';
import * as dataSelectors from '@/data/data-selectors';
import { linkIconByPlatform } from '@/widgets/components/icon';
import { OtherPlatformSvg } from '@/widgets/components/other-platform-svg';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { getPlatformFriendlyName } from '../../utils/string-utils';
import { RsuiteTooltipWrapper } from './tooltips';

const nonClickableOnlinePlatformIcons = {
  airbnb: NonClickableAirbnbIcon,
  vrbo: NonClickableVrboIcon,
  homeaway: NonClickableHomeawayIcon,
  booking: NonClickableBookingIcon,
  flipkey: NonClickableFlipkeyIcon,
  tripadvisor: NonClickableTripAdvisorIcon,
};

const newPlatformIcons = {
  airbnb: AirbnbNew,
  vrbo: VrboNew,
  homeaway: HomeawayNew,
  flipkey: FlipkeyNew,
  tripadvisor: TripadvisorNew,
};

const defaultPlatformIconsBackgroundColors = {
  airbnb: 'rgba(0,0,0,0)',
  vrbo: 'rgba(0,0,0,0)',
  flipkey: 'rgba(0,0,0,0)',
  homeaway: '#D6F1FF',
};

export function getOnlinePlatformIcon(platformName) {
  const icon = linkIconByPlatform[platformName];
  if (!icon) return OtherPlatformSvg;
  return icon;
}

export function getNonClickableOnlinePlatformIcon(platformName) {
  const icon = nonClickableOnlinePlatformIcons[platformName];
  if (!icon) return OtherPlatformSvg;
  return icon;
}

export function getNewPlatformIcon(platformName, staticPlatformToIconUrl) {
  if (staticPlatformToIconUrl) {
    return (props) => (
      <PlatformIcon
        {...props}
        platformName={platformName}
        platformToIconUrl={staticPlatformToIconUrl}
      />
    );
  }

  return (props) => {
    const platformToIconUrl = useSelector(dataSelectors.platformIcons) || {};
    return (
      <PlatformIcon
        {...props}
        platformName={platformName}
        platformToIconUrl={platformToIconUrl}
      />
    );
  };
}

export function getBuiltInPlatformIconComponent(platformName) {
  const icon = newPlatformIcons[platformName];
  if (!icon) return OtherPlatformSvg;
  return icon;
}

const DefaultPlatformIconBg = styled.div`
  ${({ size }) => `
    border-radius: ${size}px;
    width: ${size}px !important;
    height: ${size}px !important;
  `}
  background-color: #81CFD1 !important;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;

  font-family: League Spartan;
  font-weight: 400;
  text-transform: uppercase;
  font-size: ${({ size }) => size - 3}px;
  padding-top: 3px;
  padding-left: 1px;
`;

export function DefaultPlatformIcon({
  platform,
  style,
  className,
  size = 14,
  ...props
}) {
  return (
    <DefaultPlatformIconBg
      size={size}
      style={style}
      className={className}
      {...props}
    >
      {platform[0]}
    </DefaultPlatformIconBg>
  );
}

const PlatformIconImg = styled.img`
  ${({ size }) =>
    !size
      ? ''
      : `
    height: ${size}px;
    width: ${size}px;
  `}

  ${({ isColor }) =>
    isColor
      ? ''
      : `
    filter: grayscale(1) contrast(1.25);
  `}
`;

function PlatformIcon({ showTooltip, tooltipPosition = 'bottom', ...props }) {
  const { platformToIconUrl, platformName } = props;

  const friendlyName = getPlatformFriendlyName(platformName, platformToIconUrl);

  if (!showTooltip) {
    return <PlatformIconWithoutTooltip {...props} />;
  }

  return (
    <RsuiteTooltipWrapper text={friendlyName} tooltipPosition={tooltipPosition}>
      <span>
        <PlatformIconWithoutTooltip {...props} />
      </span>
    </RsuiteTooltipWrapper>
  );
}

function PlatformIconWithoutTooltip({
  platformToIconUrl,
  platformName,
  size = 14,
  ...props
}) {
  const customIconConfig = platformToIconUrl[platformName];
  const customIconUrl = customIconConfig?.url;

  if (!customIconUrl) {
    const IconComponent = newPlatformIcons[platformName];

    if (!IconComponent) {
      return (
        <DefaultPlatformIcon
          platform={platformName}
          {...props}
          alt={platformName}
        />
      );
    }

    return <IconComponent {...props} alt={platformName} />;
  }

  const iconUrl = customIconUrl.startsWith('https://')
    ? customIconUrl
    : `${appSettings.hostedAssetsUrl}/${customIconUrl}`;

  return (
    <PlatformIconImg src={iconUrl} alt={platformName} size={size} {...props} />
  );
}

const IconBg = styled.div`
  background: ${({ backgroundColor }) => backgroundColor || '#eee'};
  ${({ size }) =>
    !size
      ? ''
      : `
  height: ${size}px;
  width: ${size}px;
  `}
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
`;

const DEFAULT_PLATFORM_ICON_BG = '#e4f5f6';
export function getNewPlatformIconWithBackground(platformName, bgColor) {
  const Icon = styled(getNewPlatformIcon(platformName))`
    top: 0;
    opacity: 0.5;
  `;

  return ({ className, style, size = 18, onClick, ...props }) => {
    const platformToIconConfig = useSelector(dataSelectors.platformIcons);
    const backgroundColor =
      bgColor ||
      platformToIconConfig?.[platformName]?.liveBackgroundColor ||
      defaultPlatformIconsBackgroundColors[platformName] ||
      DEFAULT_PLATFORM_ICON_BG;

    return (
      <IconBg
        onClick={onClick}
        className={className}
        style={style}
        size={size}
        backgroundColor={backgroundColor}
      >
        <Icon size={!size ? undefined : size - 4} {...props} />
      </IconBg>
    );
  };
}
